import { Box, SxProps, Theme } from "@mui/material";
import { DataGrid, GridColDef, GridSortModel } from "@mui/x-data-grid";
import React, { FC, useState } from "react";
import { IProductsListPayload } from "../../Services/api/products/products.interface";
import { Environment } from "../../Config/constants";

interface ITableProps {
  columns: GridColDef[];
  data: any[];
  totalCount: number;
  onSelect?: (selectedIds: any[]) => void;
  onRowClick?: (row: any) => void;
  isRowSelectable?: (params: any) => boolean;
  onPageChange?: (data: IProductsListPayload) => void;
  onSortModelChange?: (sortModel: GridSortModel) => void;
  search?: string;
  sx?: SxProps<Theme>;
  disableRowSelectionOnClick?: boolean;
  pagination?: boolean; // Flag para habilitar/desabilitar paginação
  checkboxSelection?: boolean; // Flag para habilitar/desabilitar seleção por checkbox
}

const Table: FC<ITableProps> = ({
  columns,
  data,
  totalCount,
  onSelect,
  onRowClick,
  isRowSelectable,
  onPageChange,
  onSortModelChange,
  search,
  disableRowSelectionOnClick = true,
  pagination = true,
  checkboxSelection = false,
  ...rest
}) => {
  const [page, setPage] = useState(0);

  const handleRowSelection = (selectionModel: any) => {
    if (onSelect) {
      onSelect(selectionModel);
    }
  };

  const handleRowClick = (params: any) => {
    if (onRowClick) {
      onRowClick(params.row);
    }
  };

  const handlePageChange = (newPage: number) => {
    if (newPage !== page) {
      setPage(newPage);
      if (onPageChange) {
        onPageChange({ page: newPage + 1, searchBy:search });
      }
    }
  };

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (onSortModelChange) {
      onSortModelChange(sortModel);
    }
  };

  const allRowsSelectable = data.every((row) =>
    isRowSelectable ? isRowSelectable({ row }) : true
  );

  const updatedColumns = columns.map((col) => ({
    ...col,
    sortable: col.sortable !== undefined ? col.sortable : false, // Mantém sortable se definido na coluna
    disableColumnMenu: true, // Remove os três pontinhos do menu
  }));

  return (
    <Box sx={{ minHeight: 300, width: "100%" }} {...rest}>
      <DataGrid
        rows={data}
        columns={updatedColumns}
        rowCount={pagination ? totalCount : undefined}
        paginationMode={pagination ? "server" : undefined}
        sortingMode="server"
        pageSizeOptions={pagination ? [Environment.OFFSET_REGISTERS ? Number(Environment.OFFSET_REGISTERS) : 10] : undefined}
        paginationModel={pagination ? { pageSize: Environment.OFFSET_REGISTERS ? Number(Environment.OFFSET_REGISTERS) : 10, page } : undefined}
        rowHeight={120}
        checkboxSelection={checkboxSelection}
        disableRowSelectionOnClick={disableRowSelectionOnClick}
        onRowSelectionModelChange={handleRowSelection}
        isRowSelectable={isRowSelectable}
        onRowClick={handleRowClick}
        onPaginationModelChange={pagination ? (model) => handlePageChange(model.page) : undefined}
        onSortModelChange={handleSortModelChange}
        hideFooter={!pagination} // Oculta o footer se pagination for false
        disableMultipleRowSelection // Remove checkbox do header para seleção de todos os elementos
        sx={{
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell': {
            border: 'none',
            overflow: 'visible',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
            padding: '12px 8px',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            '& > *': {
              maxWidth: '100%',
            }
          },
          '& .MuiDataGrid-row': {
            overflow: 'visible',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          },
          '& .MuiDataGrid-main': {
            overflow: 'auto',
          },
          '& .MuiDataGrid-cellContent': {
            width: '100%',
            maxWidth: '100%',
            whiteSpace: 'normal',
            wordWrap: 'break-word',
          }
        }}
      />
    </Box>
  );
};

export { Table };
